// components/atoms/LatexRenderer.jsx
// Renders LaTeX math using katex directly (not react-katex, which has CRA issues).
// katex.renderToString() produces HTML which we inject via dangerouslySetInnerHTML.
// This is safe because katex only generates math markup — no user-controlled HTML.
//
// Delimiters:
//   $$...$$  → display / block equation (centred, full width)
//   $...$    → inline equation
//
// Currency protection: "$10", "$3.50" (digit-only content) are skipped.

import React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { useTheme } from "../../context/ThemeContext";

// ── Render one LaTeX string to HTML via katex ────────────────────────
function renderMath(latex, displayMode) {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false,       // show error in red instead of crashing
      trust: false,
      strict: false,
    });
  } catch {
    return `<code style="opacity:0.6;font-size:0.88em">${latex}</code>`;
  }
}

// ── Parse raw string into segments ───────────────────────────────────
function parse(raw) {
  if (!raw || typeof raw !== "string") return [];
  const segs = [];

  // Pass 1: extract $$...$$ (block math)
  const blockRe = /\$\$([\s\S]+?)\$\$/g;
  let last = 0, m;
  while ((m = blockRe.exec(raw)) !== null) {
    if (m.index > last) segs.push({ type: "text", value: raw.slice(last, m.index) });
    segs.push({ type: "block", value: m[1].trim() });
    last = blockRe.lastIndex;
  }
  if (last < raw.length) segs.push({ type: "text", value: raw.slice(last) });

  // Pass 2: within text segments, extract $...$ (inline math)
  const inlineRe = /\$([^$\n]+?)\$/g;
  const result = [];
  for (const seg of segs) {
    if (seg.type !== "text") { result.push(seg); continue; }
    inlineRe.lastIndex = 0;
    let sub = 0, sm;
    while ((sm = inlineRe.exec(seg.value)) !== null) {
      const content = sm[1].trim();
      // Skip currency: $10, $3.50, $1,000
      if (/^\d[\d.,]*$/.test(content)) continue;
      if (sm.index > sub) result.push({ type: "text", value: seg.value.slice(sub, sm.index) });
      result.push({ type: "inline", value: content });
      sub = inlineRe.lastIndex;
    }
    if (sub < seg.value.length) result.push({ type: "text", value: seg.value.slice(sub) });
  }
  return result;
}

// ── Plain text with newline → <br> ────────────────────────────────────
function TextSeg({ value }) {
  const lines = value.split("\n");
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
      ))}
    </>
  );
}

// ── Main ─────────────────────────────────────────────────────────────
export default function LatexRenderer({ text, style = {} }) {
  const { t } = useTheme();
  if (!text) return null;
  const segs = parse(text);
  if (!segs.length) return null;

  return (
    <span style={{ lineHeight: 1.9, color: t.text, ...style }}>
      {segs.map((seg, i) => {
        if (seg.type === "block") {
          return (
            <span key={i} style={{ display: "block", margin: "16px 0", overflowX: "auto", textAlign: "center" }}
              dangerouslySetInnerHTML={{ __html: renderMath(seg.value, true) }} />
          );
        }
        if (seg.type === "inline") {
          return (
            <span key={i}
              dangerouslySetInnerHTML={{ __html: renderMath(seg.value, false) }} />
          );
        }
        return <TextSeg key={i} value={seg.value} />;
      })}
    </span>
  );
}
